import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LockClosedIcon, PhotoIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import * as api from '../../api/account'
import { useAuth } from '../../context/AuthContext'
import { Button, ErrorText, Field, SectionTitle, inputClass } from './ui'
import TabelaProgow from './TabelaProgow'
import WyborKoloru from './WyborKoloru'
import { KOLOR_DOMYSLNY } from '../../lib/kolorDokumentu'
import { zapamietajUstawieniaWyceny } from '../../hooks/useUstawieniaWyceny'
import { useMozna } from '../../hooks/usePlan'
import { KlodkaPremium } from '../plany/BramkaPremium'
import { useOknoPremium } from '../plany/OknoPremiumContext'

/**
 * Ustawienia wyceny konta FIRMOWEGO: marże, stawki montażu, VAT dokumentu „Oferta i Projekt”
 * (dawniej „VoltValue Projekt”) i wygląd firmy (logo, kolor) pokazywany w zakładce „Firma”.
 *
 * PO CO TO ISTNIEJE: „VoltValue Projekt" pokazuje domyślnie rozbicie na każdy
 * komponent i usługę. Firma instalatorska używa tego samego dokumentu jako
 * załącznika do oferty dla klienta końcowego — a wtedy rozpisane ceny zakupu
 * są ostatnią rzeczą, jaką chce tam pokazać.
 *
 * Sekcje siedzą w jednym pliku, bo dzielą pobieranie i zapis tych samych
 * ustawień (runda 6 dołożyła magazyn energii w marżach i montażu oraz usługi dodatkowe). Rozbicie na trzy pliki znaczyłoby albo trzy kopie tego hooka, albo
 * czwarty plik na sam stan — przy tej wielkości to więcej szwów niż pożytku.
 */

// Wgrany plik może być duży, bo i tak zmniejszamy go przed zapisem. Ten limit
// chroni tylko przed wczytaniem do pamięci czegoś absurdalnego.
const MAX_PLIKU_BAJTOW = 5 * 1024 * 1024

// Twardy sufit tego, co WYSYŁAMY. Musi zgadzać się z `MAX_LOGO_BAJTOW`
// w backendzie (accounts/api.py) i zmieścić się pod `client_max_body_size`
// nginxa (nginx.conf). Trzy progi na trzech piętrach — rozjazd któregokolwiek
// kończy się odrzuceniem bez czytelnego komunikatu.
const MAX_ZAPISU_BAJTOW = 1400 * 1024

/**
 * Dłuższa krawędź zapisywanego logo.
 *
 * NIE jest to liczba dobrana pod ekran. Na okładce dokumentu logo ma
 * `max-w-[240px]` (StronaTytulowa), czyli ok. 6,3 cm na papierze. Ostry druk
 * przy 300 dpi potrzebuje tam ~750 px, więc 1000 px zostawia zapas — logo ma
 * być na wydruku OSTRE, a nie „zmniejszone". Schodzenie niżej oszczędza kilka
 * kilobajtów i psuje jedyną rzecz, po której klient ocenia, czy oferta jest
 * profesjonalna.
 */
const MAX_KRAWEDZ_PX = 1000

// Mały plik zostawiamy dokładnie takim, jaki jest. Ponowne kodowanie nic tu nie
// oszczędza, a każde przejście przez canvas to strata jakości.
const BEZ_PRZETWARZANIA_PONIZEJ = 150 * 1024

const DOZWOLONE_TYPY = ['image/png', 'image/jpeg', 'image/webp']

/**
 * Zmniejsza logo przed zapisem. Zwraca data URI — własne albo oryginał.
 *
 * PO CO: logo siedzi w bazie i wraca w KAŻDEJ odpowiedzi z ustawieniami wyceny,
 * a po te sięga też kalkulacja kosztu na kaflach zestawów. Trzymanie oryginału
 * znaczyłoby megabajt pobierany przy pierwszym renderze kafli — na telefonie
 * odczuwalnie.
 *
 * Format wyjściowy to WEBP, a nie JPEG, bo logotypy bywają na przezroczystym
 * tle: JPEG nie ma kanału alfa i zamieniłby je w czarny prostokąt. Przeglądarka
 * bez obsługi WEBP zwraca z `toDataURL` PNG — też z alfą, więc gorszy tylko
 * objętościowo. Przy pliku JPEG zostajemy przy JPEG: skoro źródło nie miało
 * przezroczystości, nie ma czego chronić.
 */
const przygotujLogo = (dataUri, typPliku, rozmiarPliku) => new Promise((zwroc) => {
  const obraz = new Image()

  obraz.onload = () => {
    const najdluzsza = Math.max(obraz.width, obraz.height)
    const miesciSieWKrawedzi = najdluzsza <= MAX_KRAWEDZ_PX

    // Dwa warunki, nie jeden: obrazek bywa mały wymiarami, a ciężki bajtami
    // (nieskompresowany PNG 800×800 potrafi ważyć kilka megabajtów). Taki też
    // musi przejść przez ponowne kodowanie, choć skalować go nie ma po co.
    if (miesciSieWKrawedzi && rozmiarPliku <= BEZ_PRZETWARZANIA_PONIZEJ) {
      zwroc(dataUri)
      return
    }

    const skala = miesciSieWKrawedzi ? 1 : MAX_KRAWEDZ_PX / najdluzsza
    const plotno = document.createElement('canvas')
    plotno.width = Math.max(1, Math.round(obraz.width * skala))
    plotno.height = Math.max(1, Math.round(obraz.height * skala))

    const pedzel = plotno.getContext('2d')
    pedzel.imageSmoothingEnabled = true
    pedzel.imageSmoothingQuality = 'high'
    pedzel.drawImage(obraz, 0, 0, plotno.width, plotno.height)

    const format = typPliku === 'image/jpeg' ? 'image/jpeg' : 'image/webp'
    const zmniejszone = plotno.toDataURL(format, 0.92)

    // Gdyby kodowanie wypadło GORZEJ niż oryginał (zdarza się przy dobrze
    // skompresowanym źródle i braku WEBP), zostawiamy oryginał — jest wtedy
    // mniejszy, a limit zapisu i tak pilnuje reszty.
    zwroc(zmniejszone.length < dataUri.length ? zmniejszone : dataUri)
  }

  // Nie umiemy odczytać obrazu — oddajemy oryginał i niech zadecyduje limit
  // zapisu niżej. Cicha zamiana na nic byłaby gorsza niż czytelna odmowa.
  obraz.onerror = () => zwroc(dataUri)
  obraz.src = dataUri
})

/** Wspólne pobieranie + zapis dla wszystkich trzech zakładek. */
const useUstawieniaWyceny = (setFlash) => {
  const [dane, setDane] = useState(null)
  const { odswiezProfil } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let anulowane = false
    api.getQuoteSettings().then((res) => {
      if (anulowane) return
      if (!res.ok) { setError(res.error); return }
      setDane(res.data)
    })
    return () => { anulowane = true }
  }, [])

  const zapisz = async (zmiany) => {
    setError('')
    setBusy(true)
    const res = await api.updateQuoteSettings(zmiany)
    setBusy(false)
    if (!res.ok) { setError(res.error); return false }
    setDane(res.data)
    // Dokument czyta ustawienia z pamięci modułu; bez tego nowe logo albo kolor pokazałyby się dopiero po odświeżeniu.
    zapamietajUstawieniaWyceny(res.data)
    // Logo firmy stoi też w pasku zamiast litery konta: nowa wersja w profilu zmienia obrazek od razu.
    if ('logo_data_uri' in zmiany) odswiezProfil()
    setFlash?.('Zapisano ustawienia wyceny.')
    return true
  }

  return { dane, setDane, busy, error, setError, zapisz }
}

/** Nazwa tabeli w sekcji, gdy jest ich kilka. */
const NazwaTabeli = ({ children, className = '' }) => (
  <h3 className={`mb-2 text-sm font-semibold text-gray-900 ${className}`}>{children}</h3>
)

const Ladowanie = () => <p className="text-sm text-gray-500 py-6">Wczytuję ustawienia…</p>

const TylkoPodglad = () => (
  <p className="mb-5 flex items-center gap-2 text-sm text-gray-600">
    <LockClosedIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
    Wycena jest wspólna dla firmy. Zmienia ją konto główne albo osoba z uprawnieniem „Zmiana kosztów wewn.”.
  </p>
)

// ── Wygląd firmy (zakładka „Firma”): logo i kolor na wszystkich dokumentach ──
// Decyzja właściciela 2026-10-11: logo i kolor ustawia się raz, przy danych firmy, a nie w ustawieniach
// jednego dokumentu. Zapis nadal przez `/quote-settings` (te same pola, bez zmian w backendzie).
// Plany (FE-05, 2026-10-19): logo i kolor firmy w dokumentach to funkcja Premium `marka_dokumentu`. We Free
// dokumenty mają logo i kolor VoltValue, a wybór tutaj jest wyłączony z plakietką Premium (zapisane zostają).

export const WygladFirmySection = ({ setFlash }) => {
  const { dane, setDane, busy, error, setError, zapisz } = useUstawieniaWyceny(setFlash)
  const { mozna: planMarki } = useMozna('marka_dokumentu')
  const { otworz: otworzPremium } = useOknoPremium()
  if (!dane) return error ? <ErrorText>{error}</ErrorText> : <Ladowanie />
  const bezUprawnienia = dane.mozna_zmieniac === false
  const zablokowane = bezUprawnienia || !planMarki

  const ustaw = (zmiany) => setDane({ ...dane, ...zmiany })

  const wczytajLogo = (plik) => {
    if (!plik) return
    setError('')
    if (!DOZWOLONE_TYPY.includes(plik.type)) {
      setError('Logo musi być plikiem PNG, JPG albo WEBP.')
      return
    }
    if (plik.size > MAX_PLIKU_BAJTOW) {
      setError('Logo jest za duże, wybierz plik do 5 MB.')
      return
    }
    const czytnik = new FileReader()
    czytnik.onload = async () => {
      const gotowe = await przygotujLogo(String(czytnik.result), plik.type, plik.size)
      // Ostatnia bramka przed wysyłką: bez niej nginx odbiłby żądanie kodem 413 bez czytelnego komunikatu.
      if (gotowe.length > MAX_ZAPISU_BAJTOW) {
        setError('Nie udało się zmniejszyć tego logo, wybierz mniejszy plik.')
        return
      }
      ustaw({ logo_data_uri: gotowe })
    }
    czytnik.onerror = () => setError('Nie udało się odczytać pliku.')
    czytnik.readAsDataURL(plik)
  }

  return (
    <form className="mt-10 border-t border-gray-200 pt-8" onSubmit={(e) => {
      e.preventDefault()
      zapisz({
        logo_data_uri: dane.logo_data_uri || '',
        document_color: dane.document_color || KOLOR_DOMYSLNY,
      })
    }}>
      <SectionTitle hint="Logo i kolor firmy na ofertach, projektach i innych dokumentach dla klienta.">
        <span className="inline-flex items-center gap-2">Wygląd firmy {!planMarki && <KlodkaPremium />}</span>
      </SectionTitle>
      {!planMarki && (
        <p className="mb-5 text-sm text-gray-600">
          W planie Free oferta i projekt mają logo i niebieski kolor VoltValue. Logo i kolor firmy są w planie Premium.{' '}
          <button type="button" onClick={() => otworzPremium('marka_dokumentu')}
                  className="font-semibold text-blue-700 hover:underline">Co daje Premium</button>
        </p>
      )}
      {planMarki && bezUprawnienia && <TylkoPodglad />}
      <fieldset disabled={zablokowane} className="m-0 min-w-0 border-0 p-0">

      <div className="mb-6" data-samouczek="logo">
        <p className="text-sm font-medium text-gray-700 mb-1.5">Logo</p>
        <p className="text-xs text-gray-400 mb-3">
          PNG, JPG albo WEBP, do 5&nbsp;MB. Zmniejszymy je automatycznie, zachowując jakość potrzebną do druku.
        </p>

        {dane.logo_data_uri ? (
          <div className="flex items-center gap-4">
            {/* Białe tło pod podglądem także w ciemnym motywie: logo firmy jest zwykle przygotowane pod jasny papier. */}
            <div className="vv-arkusz rounded-lg border border-gray-200 p-2">
              <img src={dane.logo_data_uri} alt="Logo firmy" className="h-12 w-auto max-w-[180px]" />
            </div>
            <button
              type="button" disabled={busy}
              onClick={() => ustaw({ logo_data_uri: '' })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200
                         text-sm text-gray-700 hover:text-red-600 hover:border-red-200 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Usuń
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200
                            text-sm text-gray-700 cursor-pointer hover:border-blue-300 hover:text-blue-700
                            transition-colors">
            <PhotoIcon className="w-4 h-4" />
            Wybierz plik
            <input type="file" className="sr-only" accept="image/png,image/jpeg,image/webp"
                   disabled={busy}
                   onChange={(e) => wczytajLogo(e.target.files?.[0])} />
          </label>
        )}
      </div>

      <WyborKoloru
        kolor={dane.document_color}
        busy={busy}
        onZmiana={(hex) => ustaw({ document_color: hex })}
      />

      </fieldset>
      <ErrorText>{error}</ErrorText>
      {!zablokowane && <div className="mt-6"><Button type="submit" busy={busy}>Zapisz wygląd</Button></div>}
    </form>
  )
}

// ── Zakładka „Oferta i Projekt”: stawki VAT dokumentu ────────────────────────
// Cena w dokumencie jest zawsze końcowa, netto i brutto (decyzja właściciela 2026-10-11), więc przełącznik
// „tylko cena końcowa” zniknął z ekranu; jego wartość zapisujemy dalej bez zmian.

export const ProjektSection = ({ setFlash }) => {
  const { dane, setDane, busy, error, zapisz } = useUstawieniaWyceny(setFlash)
  if (!dane) return error ? <ErrorText>{error}</ErrorText> : <Ladowanie />
  // Wycena jest wspólna dla firmy; bez uprawnienia `wycena` osoba z zespołu tylko ją ogląda.
  const zablokowane = dane.mozna_zmieniac === false

  const ustaw = (zmiany) => setDane({ ...dane, ...zmiany })

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      zapisz({
        only_final_price: dane.only_final_price,
        vat_rate: Number(dane.vat_rate),
        source_vat_rate: Number(dane.source_vat_rate),
      })
    }}>
      <SectionTitle hint="Dokument, który wysyłasz klientowi jako ofertę. Cena idzie w nim jako kwota końcowa, netto i brutto.">
        Oferta i Projekt
      </SectionTitle>
      {zablokowane && <TylkoPodglad />}
      <fieldset disabled={zablokowane} className="m-0 min-w-0 border-0 p-0">

      <p className="mb-6 text-sm text-gray-600">
        Logo i kolor dokumentu ustawiasz w zakładce{' '}
        <Link to="/konto?sekcja=firma" className="font-semibold text-blue-700 hover:underline">Firma</Link>.
      </p>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1.5">Stawki VAT</p>
        <p className="text-xs text-gray-400 mb-3 leading-5">
          Obniżona stawka dotyczy <strong className="font-semibold text-gray-500">usługi
          montażu</strong>, a nie sprzętu, dlatego są dwa pola. Sprzęt, dostawa
          i konstrukcja idą stawką pierwszą, montaż (robocizna z materiałami pomocniczymi) drugą. Marża doliczana
          jest na końcu, bez podatku. Przy równych stawkach kwota nie zmienia się
          ani o grosz.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="VAT sprzętu, dostawy i konstrukcji (%)"
                 hint="Stawka, w której podane są ceny sklepów. Zwykle 23%.">
            <input type="number" min="0" max="100" step="0.5" className={inputClass}
                   value={dane.source_vat_rate} disabled={busy}
                   onChange={(e) => ustaw({ source_vat_rate: e.target.value })} />
          </Field>
          <Field label="VAT montażu (%)"
                 hint="Stawka dla montażu. Dla budynków mieszkalnych zwykle 8%.">
            <input type="number" min="0" max="100" step="0.5" className={inputClass}
                   value={dane.vat_rate} disabled={busy}
                   onChange={(e) => ustaw({ vat_rate: e.target.value })} />
          </Field>
        </div>
        <p className="mt-1 text-xs text-gray-400 leading-5">
          Za poprawność zastosowanej stawki wobec klienta odpowiada Twoja firma;
          my tylko liczymy według tego, co tu ustawisz.
        </p>
      </div>

      </fieldset>
      <ErrorText>{error}</ErrorText>
      {!zablokowane && <div className="mt-6"><Button type="submit" busy={busy}>Zapisz zmiany</Button></div>}
    </form>
  )
}

// ── Zakładka 2: marże ────────────────────────────────────────────────────────

export const MarzeSection = ({ setFlash }) => {
  const { dane, setDane, busy, error, zapisz } = useUstawieniaWyceny(setFlash)
  if (!dane) return error ? <ErrorText>{error}</ErrorText> : <Ladowanie />
  // Wycena jest wspólna dla firmy; bez uprawnienia `wycena` osoba z zespołu tylko ją ogląda.
  const zablokowane = dane.mozna_zmieniac === false

  const procentowo = dane.margin_mode === 'percent'

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      zapisz({
        margin_mode: dane.margin_mode,
        margin_ranges: dane.margin_ranges,
        storage_margin_ranges: dane.storage_margin_ranges || [],
      })
    }}>
      {/* `data-samouczek`: miejsce podświetlane w samouczku „Pierwsze kroki” (krok „Twój cennik”). */}
      <div data-samouczek="marze">
        <SectionTitle hint="Marża doliczana do kwoty netto zestawu, zależnie od mocy instalacji. Widzi ją tylko Twoja firma, na dokumencie dla klienta jest już w cenie końcowej.">
          Marże
        </SectionTitle>
      </div>
      {zablokowane && <TylkoPodglad />}
      <fieldset disabled={zablokowane} className="m-0 min-w-0 border-0 p-0">

      {/* Kwotowo ALBO procentowo, nigdy oba naraz: dwie marże działające
          jednocześnie dają kwotę, której nie da się wytłumaczyć klientowi. */}
      <div role="group" aria-label="Sposób naliczania marży"
           className="inline-flex rounded-lg border border-gray-200 overflow-hidden mb-5">
        {[
          { tryb: 'amount', label: 'Kwotowo (zł netto)' },
          { tryb: 'percent', label: 'Procentowo' },
        ].map(({ tryb, label }) => (
          <button
            key={tryb} type="button" disabled={busy}
            onClick={() => setDane({ ...dane, margin_mode: tryb })}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              dane.margin_mode === tryb
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <NazwaTabeli>Instalacja fotowoltaiczna</NazwaTabeli>
      <TabelaProgow
        progi={dane.margin_ranges}
        onChange={(progi) => setDane({ ...dane, margin_ranges: progi })}
        disabled={busy}
        kolumny={procentowo
          ? [{ klucz: 'procent', naglowek: 'Marża', jednostka: '%', placeholder: 'np. 12' }]
          : [{ klucz: 'kwota', naglowek: 'Marża', jednostka: 'zł netto', placeholder: 'np. 4000' }]}
      />

      {/* Runda 6: marża za magazyn, progi po pojemności. Ten sam tryb co wyżej, doliczana obok marży
          instalacji, gdy zestaw ma magazyn (procentowo: od ceny magazynu z montażem). */}
      <NazwaTabeli className="mt-8">Magazyn energii</NazwaTabeli>
      <TabelaProgow
        progi={dane.storage_margin_ranges || []}
        onChange={(progi) => setDane({ ...dane, storage_margin_ranges: progi })}
        disabled={busy}
        nazwaZakresu="Pojemność magazynu"
        jednostkaZakresu="kWh"
        kolumny={procentowo
          ? [{ klucz: 'procent', naglowek: 'Marża', jednostka: '%', placeholder: 'np. 8' }]
          : [{ klucz: 'kwota', naglowek: 'Marża', jednostka: 'zł netto', placeholder: 'np. 1500' }]}
      />
      <p className="mt-2 text-xs text-gray-400 leading-5">
        Doliczana do marży instalacji, gdy zestaw ma magazyn.
        {procentowo ? ' Procent liczony od ceny magazynu razem z jego montażem.' : ''}
      </p>

      <p className="mt-3 text-xs text-gray-400 leading-5">
        Przełączenie trybu nie kasuje drugiej kolumny: kwoty i procenty zapisują
        się osobno, więc możesz wrócić bez przepisywania tabeli.
      </p>

      </fieldset>
      <ErrorText>{error}</ErrorText>
      {!zablokowane && <div className="mt-6"><Button type="submit" busy={busy}>Zapisz zmiany</Button></div>}
    </form>
  )
}

// ── Zakładka 3: stawki montażu ───────────────────────────────────────────────

export const MontazSection = ({ setFlash }) => {
  const { dane, setDane, busy, error, zapisz } = useUstawieniaWyceny(setFlash)
  if (!dane) return error ? <ErrorText>{error}</ErrorText> : <Ladowanie />
  // Wycena jest wspólna dla firmy; bez uprawnienia `wycena` osoba z zespołu tylko ją ogląda.
  const zablokowane = dane.mozna_zmieniac === false

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      zapisz({ install_ranges: dane.install_ranges, storage_install_ranges: dane.storage_install_ranges || [] })
    }}>
      <SectionTitle hint="Stawki montażu zależne od wielkości instalacji i pojemności magazynu. Zastępują nasze wartości domyślne w asystencie doboru, w wycenie klienta i w dokumencie projektu.">
        Stawki montażu
      </SectionTitle>
      {zablokowane && <TylkoPodglad />}
      <fieldset disabled={zablokowane} className="m-0 min-w-0 border-0 p-0">

      <NazwaTabeli>Instalacja fotowoltaiczna</NazwaTabeli>
      <TabelaProgow
        progi={dane.install_ranges}
        onChange={(progi) => setDane({ ...dane, install_ranges: progi })}
        disabled={busy}
        kolumny={[{ klucz: 'wartosc', naglowek: 'Stawka', jednostka: 'zł/kWp', placeholder: 'np. 950' }]}
      />

      <p className="mt-3 text-xs text-gray-400 leading-5">
        Stawka obejmuje robociznę razem z materiałami pomocniczymi: okablowaniem,
        zabezpieczeniami AC/DC i drobnym osprzętem. Konstrukcja montażowa liczona jest
        osobno i te dwie pozycje nie mogą się nakładać.
      </p>

      <NazwaTabeli className="mt-8">Magazyn energii</NazwaTabeli>
      <TabelaProgow
        progi={dane.storage_install_ranges || []}
        onChange={(progi) => setDane({ ...dane, storage_install_ranges: progi })}
        disabled={busy}
        nazwaZakresu="Pojemność magazynu"
        jednostkaZakresu="kWh"
        kolumny={[{ klucz: 'wartosc', naglowek: 'Stawka', jednostka: 'zł/kWh', placeholder: 'np. 100' }]}
      />
      <p className="mt-3 text-xs text-gray-400 leading-5">
        Stawka za kWh pojemności, doliczana, gdy zestaw ma magazyn. Puste pole: montażu magazynu nie doliczamy.
      </p>

      </fieldset>
      <ErrorText>{error}</ErrorText>
      {!zablokowane && <div className="mt-6"><Button type="submit" busy={busy}>Zapisz zmiany</Button></div>}
    </form>
  )
}

// ── Zakładka „Usługi dodatkowe” (runda 6, 2026-10-12) ───────────────────────
// Dowolne pozycje z ceną netto i własną stawką VAT. Handlowiec włącza je przełącznikiem przy cenie klienta
// („Z asystenta doboru”), a cena doliczana jest do ceny instalacji (`lib/wycena`, `wybraneUslugi`).

/** Stawki do wyboru. Stawka spoza listy (zapisana inaczej) zostaje dopisana, żeby jej nie zgubić. */
export const STAWKI_VAT_USLUG = [23, 8, 5, 0]

// Nowa pozycja dostaje id przy zapisie w backendzie (`_oczysc_uslugi`); do tego czasu klucz wiersza jest lokalny.
let licznikNowych = 0
const nowaUsluga = () => ({ id: '', nazwa: '', netto: null, vat: 23, klucz: `nowa-${(licznikNowych += 1)}` })

export const UslugiSection = ({ setFlash }) => {
  const { dane, setDane, busy, error, setError, zapisz } = useUstawieniaWyceny(setFlash)
  if (!dane) return error ? <ErrorText>{error}</ErrorText> : <Ladowanie />
  // Wycena jest wspólna dla firmy; bez uprawnienia `wycena` osoba z zespołu tylko ją ogląda.
  const zablokowane = dane.mozna_zmieniac === false
  const uslugi = dane.extra_services || []

  const ustawUslugi = (lista) => setDane({ ...dane, extra_services: lista })
  const ustawPole = (i, pole, wartosc) => ustawUslugi(uslugi.map((u, idx) => (idx === i ? { ...u, [pole]: wartosc } : u)))

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      if (uslugi.some((u) => !String(u.nazwa || '').trim())) {
        setError('Każda usługa musi mieć nazwę.')
        return
      }
      zapisz({
        extra_services: uslugi.map(({ id, nazwa, netto, vat }) => ({
          id: id || '', nazwa: String(nazwa || '').trim(), netto: netto == null || netto === '' ? 0 : Number(netto), vat: Number(vat),
        })),
      })
    }}>
      <SectionTitle hint="Usługi, które możesz doliczyć do ceny instalacji przy wycenie klienta. Cena netto i stawka VAT dla każdej usługi.">
        Usługi dodatkowe
      </SectionTitle>
      {zablokowane && <TylkoPodglad />}
      <fieldset disabled={zablokowane} className="m-0 min-w-0 border-0 p-0">

      <div className="rounded-xl border border-blue-100 overflow-hidden max-w-2xl">
        <div className="hidden sm:flex items-center gap-3 px-3 py-2.5 bg-blue-50 border-b border-blue-100
                        text-[11px] font-semibold text-blue-700">
          <span className="flex-1">Nazwa usługi</span>
          <span className="w-36 flex-shrink-0">Cena netto</span>
          <span className="w-24 flex-shrink-0">VAT</span>
          <span className="w-8 flex-shrink-0" aria-hidden="true" />
        </div>

        <div className="divide-y divide-gray-100">
          {uslugi.length === 0 && <p className="px-3 py-3 text-sm text-gray-500">Brak usług. Dodaj pierwszą poniżej.</p>}
          {uslugi.map((u, i) => {
            const stawki = STAWKI_VAT_USLUG.includes(Number(u.vat)) ? STAWKI_VAT_USLUG : [...STAWKI_VAT_USLUG, Number(u.vat)]
            return (
              <div key={u.id || u.klucz || i} className="flex flex-wrap sm:flex-nowrap items-center gap-3 px-3 py-2">
                <input
                  type="text" maxLength={80} value={u.nazwa || ''} disabled={busy}
                  aria-label={`Usługa ${i + 1}, nazwa`} placeholder="np. Przegląd po roku"
                  onChange={(e) => ustawPole(i, 'nazwa', e.target.value)}
                  className={`${inputClass} flex-1 min-w-[12rem]`}
                />
                <span className="w-36 flex-shrink-0 flex items-center gap-1.5">
                  <input
                    type="number" min="0" step="any" value={u.netto ?? ''} disabled={busy}
                    aria-label={`Usługa ${i + 1}, cena netto`} placeholder="0"
                    onChange={(e) => ustawPole(i, 'netto', e.target.value === '' ? null : Number(e.target.value))}
                    className={`${inputClass} min-w-0 tabular-nums`}
                  />
                  <span className="text-xs text-gray-400 whitespace-nowrap">zł</span>
                </span>
                <span className="w-24 flex-shrink-0">
                  <select
                    value={Number(u.vat)} disabled={busy} aria-label={`Usługa ${i + 1}, stawka VAT`}
                    onChange={(e) => ustawPole(i, 'vat', Number(e.target.value))}
                    className={inputClass}
                  >
                    {stawki.map((v) => <option key={v} value={v}>{v}%</option>)}
                  </select>
                </span>
                <button
                  type="button" onClick={() => ustawUslugi(uslugi.filter((_, idx) => idx !== i))} disabled={busy}
                  className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg text-gray-300
                             hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                  aria-label={`Usuń usługę ${i + 1}`}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            )
          })}
        </div>

        <div className="px-3 py-2.5 bg-gray-50 border-t border-gray-100">
          <button
            type="button" onClick={() => ustawUslugi([...uslugi, nowaUsluga()])} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-blue-700
                       hover:bg-blue-100 transition-colors disabled:opacity-40"
          >
            <PlusIcon className="w-4 h-4" />
            Dodaj usługę
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400 leading-5">
        Usługę włączasz przy cenie klienta, w karcie klienta. Doliczamy ją netto, a VAT w stawce tej usługi.
      </p>

      </fieldset>
      <ErrorText>{error}</ErrorText>
      {!zablokowane && <div className="mt-6"><Button type="submit" busy={busy}>Zapisz zmiany</Button></div>}
    </form>
  )
}
